import {
  forwardRef,
  ForwardRefExoticComponent,
  InputHTMLAttributes,
  Ref,
  RefAttributes,
} from 'react';
import styled, { css } from 'styled-components';
import isEqual from 'lodash/isEqual';

interface IInputStatic {
  Content: any;
  Error: any;
  Input: any;
  Label: any;
  Wrapper: any;
}
interface IAllInputProps extends InputHTMLAttributes<HTMLInputElement> {
  'data-qa': string;
  className?: string;
  errorMessage?: string | boolean;
  hasError?: boolean;
  hasFloatingLabel?: boolean;
  id?: string;
  inputmode?: string;
  label?: string;
  name: string;
  prefix?: string;
  suffix?: string;
  showLabel?: boolean;
}

type InputComponent = ForwardRefExoticComponent<IInputProps & RefAttributes<HTMLInputElement>> &
  IInputStatic;

export type IInputProps = Omit<IAllInputProps, 'placeholder'>;

const ensureEmptyStringIfNil = (param: any) => {
  if (isEqual(param, 0)) {
    return '0';
  }

  return param || '';
};

// eslint-disable-next-line react/display-name
export const Input = forwardRef(
  (
    {
      errorMessage,
      className,
      hasError,
      hasFloatingLabel = true,
      label,
      name,
      value,
      prefix,
      suffix,
      showLabel,
      ...props
    }: IInputProps,
    ref?: Ref<HTMLInputElement>,
  ) => {
    return (
      <Input.Content className={className} $hasError={hasError} $prefix={prefix} $suffix={suffix}>
        <Input.Wrapper>
          <Input.Input
            aria-labelledby={name}
            name={name}
            placeholder={hasFloatingLabel || showLabel ? '' : label}
            ref={ref}
            value={ensureEmptyStringIfNil(value)}
            $prefix={prefix}
            $suffix={suffix}
            {...props}
          />
          <Input.Label
            hasFloatingLabel={hasFloatingLabel}
            hasValue={!!value}
            id={name}
            showLabel={showLabel}
          >
            {hasFloatingLabel || showLabel ? label : name}
          </Input.Label>
        </Input.Wrapper>
        {hasError && (
          <Input.Error data-qa={`${props['data-qa']}_error`}>{errorMessage}</Input.Error>
        )}
      </Input.Content>
    );
  },
) as InputComponent;

Input.Content = styled.div<{
  $hasError: boolean;
  $suffix?: string;
  $prefix?: string;
}>`
  width: 100%;
  margin-bottom: ${({ $hasError }) => ($hasError ? '32px' : '24px')};
  > div:first-child:before {
    content: '${({ $prefix }) => $prefix}';
    position: absolute;
    margin-right: 16px;
    top: 4px;
  }
  > div:first-child:after {
    content: '${({ $suffix }) => $suffix}';
    position: absolute;
    right: 0;
    top: 4px;
  }
`;

Input.Wrapper = styled.div`
  position: relative;
`;

Input.Input = styled.input<{
  $suffix?: string;
  $prefix?: string;
}>`
  display: block;
  border: none;
  border-bottom: 1px solid #000000;
  border-radius: 0;
  padding: 4px 0 8px;
  width: 100%;
  line-height: 14px;
  font-size: 11.5px;
  &:focus {
    outline: none;
    border-bottom-width: 2px;
    /* this ensures the height does not change when we increase border-bottom */
    padding-bottom: 7px;
  }

  padding-right: ${({ $suffix }) => ($suffix ? '12px' : '0px')};
  padding-left: ${({ $prefix }) => ($prefix ? '12px' : '0px')};
`;

const activeStyle = css`
  color: inherit;
  transform: translateY(-18px);
  line-height: 12px;
  font-size: 9.5px;
`;

// Input.Label represents the input placeholder. The placeholder animates/moves above
// the input when the input is in focus or contains a value
Input.Label = styled.label<{
  showLabel: boolean;
  hasFloatingLabel: boolean;
  hasValue: boolean;
}>`
  position: absolute;
  pointer-events: none;
  transition: 0.2s ease all;
  left: 0;
  bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 14px;
  font-size: 11.5px;
  visibility: ${({ hasFloatingLabel, showLabel }) =>
    hasFloatingLabel || showLabel ? 'visible' : 'hidden'};

  ${({ hasValue, showLabel }) => (hasValue || showLabel) && activeStyle};

  ${Input.Input}:focus ~ & {
    ${activeStyle};
  }
`;

Input.Error = styled.div`
  color: #e80d18;
  line-height: 9px;
  font-size: 9.5px;
  margin-top: 8px;
`;
